import { useEffect } from 'react';
import {
  dispatch,
  FileUploadOutput,
  getChannel,
  getWorkspaceId,
  updateChannel,
  useAppSelector,
  useGetMessageMediaUrlMutation,
  useLazyGetChannelDetailsQuery,
} from '@zonofi/zono-money-store';
import { v4 as uuidv4 } from 'uuid';
import { UploadToS3 } from '@zonofi/common/functions';

interface UseUploadAndGetPresignedUrlProps {
  customerId: string;
}

export const useUploadAndGetPresignedUrl = ({
  customerId,
}: UseUploadAndGetPresignedUrlProps) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const channel = useAppSelector(getChannel);

  const [channelDetailsApi] = useLazyGetChannelDetailsQuery();
  const [fetchMediaUrl] = useGetMessageMediaUrlMutation();

  useEffect(() => {
    if (customerId) {
      const payload = {
        workspaceId: sellerWorkspaceId,
        sellerWorkspaceId: sellerWorkspaceId,
        customerId: customerId,
        channelType: 'C',
      };
      channelDetailsApi(payload)?.then((res) => {
        if (res?.data) {
          dispatch(updateChannel(res.data));
        }
      });
    }
  }, [customerId]);

  const getFilePresignedUrl = async (
    file: FileUploadOutput,
    messageId: string,
    method: 'put' | 'get'
  ): Promise<string | null> => {
    try {
      if (!channel?.id) return null;
      const contentType =
        file?.type || file?.file?.type || 'application/octet-stream';

      const presignedUrl = await fetchMediaUrl({
        fileName: `/${file?.name}`,
        type: contentType.startsWith('image') ? 'image' : 'file',
        contentType: contentType,
        method: method,
        channelId: channel?.id,
        messageId,
      });

      return presignedUrl?.data || null;
    } catch (error) {
      console.log('Error getting file presigned URL', error);
      return null;
    }
  };

  const uploadAndGetPresignedUrl = async (media: FileUploadOutput) => {
    try {
      if (!channel?.id) return null;
      const contentType =
        media?.type || media?.file?.type || 'application/octet-stream';
      const messageId = uuidv4();

      const uploadPresignedUrl = await getFilePresignedUrl(
        media,
        messageId,
        'put'
      );

      if (uploadPresignedUrl && media?.uri) {
        await UploadToS3(uploadPresignedUrl, {
          uri: media.uri.toString(),
          type: contentType,
        });
      }

      const downloadPresignedUrl = await getFilePresignedUrl(
        media,
        messageId,
        'get'
      );
      return downloadPresignedUrl || null;
    } catch (error) {
      console.log('uploadAndGetPresignedUrl error:', error);
      return null;
    }
  };

  return { uploadAndGetPresignedUrl };
};
